import { File, FileText, Image, Mail, Phone } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface ArtifactTypeBreakdownProps {
  artifactTypeCounts: Record<string, number>;
}

const getArtifactTypeConfig = (type: string) => {
  const configs: Record<string, { label: string; color: string; icon: any }> = {
    DOCUMENT: {
      label: 'Document',
      color: 'bg-blue-100 text-blue-800',
      icon: FileText,
    },
    EMAIL: {
      label: 'Email',
      color: 'bg-green-100 text-green-800',
      icon: Mail,
    },
    IMAGE: {
      label: 'Image',
      color: 'bg-purple-100 text-purple-800',
      icon: Image,
    },
    AUDIO: {
      label: 'Audio',
      color: 'bg-orange-100 text-orange-800',
      icon: Phone,
    },
    UNKNOWN: {
      label: 'Unknown',
      color: 'bg-gray-100 text-gray-800',
      icon: File,
    },
  };

  return configs[type] || configs.UNKNOWN;
};

export const ArtifactTypeBreakdown = ({
  artifactTypeCounts,
}: ArtifactTypeBreakdownProps) => {
  const totalCount = Object.values(artifactTypeCounts).reduce(
    (sum, count) => sum + count,
    0,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Artifact Types</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {Object.entries(artifactTypeCounts)
            .sort(([, a], [, b]) => b - a) // Sort by count descending
            .map(([type, count]) => {
              const percentage =
                totalCount > 0 ? (count / totalCount) * 100 : 0;
              const config = getArtifactTypeConfig(type);
              const IconComponent = config.icon;

              return (
                <div key={type} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <IconComponent className="h-4 w-4 text-gray-500" />
                      <Badge variant="secondary" className={config.color}>
                        {config.label}
                      </Badge>
                    </div>
                    <span className="text-sm font-medium">
                      {count} mentions
                    </span>
                  </div>
                  <span className="text-sm font-semibold">
                    {percentage.toFixed(1)}%
                  </span>
                </div>
              );
            })}
        </div>
      </CardContent>
    </Card>
  );
};
